<template>
  <v-container>
    <v-row>
      <v-col cols="12" lg="4" class="pa-1">
        <v-card class="ma-0">
          <v-toolbar color="primary" dark>
            <v-card-title>
              Add Lost Product
            </v-card-title>
          </v-toolbar>
          <v-row class="mx-2">
            <v-col cols="12">
              <v-alert v-for="(item, index) in lostInvErrors" :key="index" dense type="error" outlined text
                       dismissible transition="slide-x-transition" class="py-0 text-uppercase">
                {{ item[0] }}
              </v-alert>
            </v-col>
            <v-col cols="12">
              <v-autocomplete v-model="supplierProduct" :search-input.sync="searchInput" label="Stock Code"
                              :items="splrPdList" item-value="stock_id" :loading="splrPdLoading" no-filter return-object>
                <template v-slot:selection="{ item }">
                  <span>{{ item.supplier_id }}-{{ item.stock_code }}</span>
                  <span class="blue-grey--text mx-1 text-caption">{{ item.product_name }} ({{ item.brand_name
                  }}})</span>
                </template>
                <template v-slot:item="{ item }">
                  <span class="blue--text">{{ item.supplier_id }}-{{ item.stock_code }}</span>
                  <span class="mx-1" />
                  <small>{{ item.product_name }} ({{ item.brand_name }})</small>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="dateTransaction" label="Transaction Date" type="date"
                            :disabled="!supplierId" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model.number="cost" label="Cost Per Unit" prefix="₱" type="number"
                            :disabled="!stockId" />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model.number="quantity" type="number" :rules="quantityRules" :suffix="unit"
                            :placeholder="quantityLeft < 1 ? 'No Stock Available' : ''" :disabled="!stockId">
                <template v-slot:label>
                  <span>Quantity</span>
                  <span v-if="quantityLeft > 0" class="mx-1">({{ quantityLeft }})</span>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="notes" auto-grow no-resize rows="1" :disabled="!stockId">
                <template v-slot:label>
                  <div>
                    Notes <small>(reason)</small>
                  </div>
                </template>
              </v-textarea>
            </v-col>
            <v-col cols="12">
              <v-btn color="green" block :loading="lostInvLoading" @click="addTransaction">
                Add Lost Product
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
      <v-col cols="12" lg="8" class="pa-1">
        <v-card class="ma-0" style="overflow: auto; overflow-x: scroll">
          <v-toolbar color="primary" dark>
            <span>Unsaved Transactions</span>
            <v-spacer />
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attr }">
                <v-btn text v-bind="attr" class="ma-0" :disabled="!tempList.length" v-on="on" @click="initUpload">
                  <v-icon>mdi-upload</v-icon>
                </v-btn>
              </template>
              <span>Upload Data</span>
            </v-tooltip>
          </v-toolbar>
          <v-alert v-for="(item, index) in uploadErrors" :key="index" dense type="error" outlined text
                   dismissible transition="slide-x-transition" class="py-0 text-uppercase">
            {{ item[0] }}
          </v-alert>
          <v-card-text style="overflow: auto;">
            <v-responsive min-width="1200">
              <v-data-table :headers="headers" :items="tempList" :footer-props="{
                'items-per-page-options': [5, 10, 15, 20],
              }" :page.sync="page" :items-per-page.sync="itemsPerPage" :loading="uploading" class="elevation-0">
                <template v-slot:item="row">
                  <tr>
                    <td>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn class="mx-2" fab dark small v-bind="attrs" color="red" v-on="on"
                                 @click="deleteTempUnit(row.index + pageFirstIndex)">
                            <v-icon dark>
                              mdi-delete
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>remove</span>
                      </v-tooltip>
                    </td>
                    <td>{{ row.item.supplier_id }}-{{ row.item.stock_code }}</td>
                    <td>{{ row.item.product_name }}</td>
                    <td>{{ row.item.brand_name }}</td>
                    <td>{{ formatMoney(row.item.cost) }}</td>
                    <td>{{ row.item.quantity }} {{ row.item.unit }}</td>
                    <td>{{ formatMoney(row.item.totalAmount) }}</td>
                    <td class="truncate">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on">{{ dateFormat(row.item.date_lost) }}</span>
                        </template>
                        <span>{{ dateFormat(row.item.date_lost) }}</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-responsive>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { moneyMixin } from './../../mixins/helper.mixins'
import { mapState, mapActions } from 'vuex'
import searchDelay from '@/extra-functions/searchDelay.extra'
import indexKeyGenerator from '@/extra-functions/index-key-generator.extra'
import splrPdRepository from '@/repositories/supplier-product.repository'
import { apiVars } from '@/extra-functions/api-helper.extra'
import { errorHandler } from '../../../../extra-functions/store.extra'
import lostInvRepository from '@/repositories/lost-inventory.repository'

const splrPdSearchDelay = searchDelay()

function currentDate() {
  const date = new Date()
  const year = date.getFullYear()
  const month = (`${date.getMonth() + 1}`).padStart(2, '0')
  const day = (`${date.getDate()}`).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function inputVars() {
  return {
    supplierId: null,
    searchInput: null,
    cost: null,
    stockId: '',
    unit: '',
    unitSearchInput: null,
    quantity: null,
    dateTransaction: currentDate(),
    notes: null,
    supplierProduct: {},
  }
}

function tableVars() {
  return {
    headers: [
      { text: 'Config' },
      { text: 'Supplier-Stock ID', sortable: false },
      { text: 'Product Name', value: 'productName' },
      { text: 'Brand', value: 'brandName' },
      { text: 'Cost', value: 'cost' },
      { text: 'Quantity', value: 'quantity' },
      { text: 'Total Amount', value: 'totalAmount' },
      { text: 'Date' },
    ],
    itemsPerPage: 10,
    page: 1,
  }
}

export default {
  name: 'CreateLostInventories',
  data() {
    return {
      ...inputVars(),
      ...apiVars('splrPd'),
      ...apiVars('lostInv'),
      ...tableVars(),
      searchingSupplier: false,
      quantityRules: [
        val => {
          let ret
          if (this.quantityLeft <= 0) {
            ret = 'No Stock Available'
          } else if (this.quantityLeft >= val) {
            ret = true
          } else {
            ret = `Only has ${this.quantityLeft} ${(this.unit + '').toUpperCase()} left in stock`
          }

          return ret
        },
      ],
      tempUnits: [],
      panel: true,
    }
  },
  created() {
    this.getSplrPd()
  },
  computed: {
    ...mapState({
      verifyErrors: state => state.lostInventory.verifyErrors,
      verifying: state => state.lostInventory.verifying,
      tempList: state => state.lostInventory.tempList,
      uploading: state => state.lostInventory.storing,
      uploadSuccess: state => state.lostInventory.storeSuccess,
      uploadErrors: state => state.lostInventory.storeErrors,
      saleInventoryUpdated: state => state.saleInventory.channelUpdate,
      recievedInventoryUpdated: state => state.recievedInventory.channelUpdate,
      lostInventoryUpdated: state => state.lostInventory.channelUpdate,
      loanInventoryUpdated: state => state.consumerLoanInventory.channelUpdate,
      storeId: state => state.settings.storeId,
    }),
    quantityLeft() {
      const stockId = this.stockId
      const supplierId = this.supplierId
      const supplierProduct = this.supplierProduct ? this.supplierProduct.qRemaining : 0
      const quantity = this.tempList.filter(e => (stockId === e.stock_id && supplierId === e.supplier_id))
        .reduce((a, b) => a + parseFloat(b.quantity), 0)
      return supplierProduct - quantity
    },
    pageFirstIndex() {
      return (this.page - 1) * this.itemsPerPage
    },
  },
  watch: {
    searchInput() {
      this.getSplrPd()
    },
    supplierProduct(val) {
      if (!val) {
        this.supplierId = null
        this.stockId = null
        this.cost = null
        this.unit = null
        return
      }
      this.supplierId = val.supplier_id
      this.stockId = val.stock_id
      this.cost = val.cost
      this.unit = val.unit
    },
    supplierProductsUpdated() {
      this.getSplrPd()
    },
    uploadSuccess(val) {
      if (val) {
        this.clearTempList()
        this.reset(inputVars())
      }
    },
    saleInventoryUpdated() {
    },
    lostInventoryUpdated() {
    },
    loanInventoryUpdated() {
    },
    recievedInventoryUpdated() {
    },
  },
  methods: {
    ...mapActions({
      checkErrors: 'lostInventory/checkErrors',
      uploadData: 'lostInventory/store',
      addTempItem: 'lostInventory/addTempItem',
      removeTempItem: 'lostInventory/removeTempItem',
      clearTempList: 'lostInventory/clearTempList',
    }),
    filterComboBox(item, queryText, itemText) {
      const regX = new RegExp(queryText, 'i')
      return regX.test(`${item.unit}${item.description}`)
    },
    initFetchSupplierProducts() {
      this.fetchSupplierProducts(this.supplierId)
    },
    initUpload() {
      const data = this.tempList.map(e => {
        return {
          index_key: e.index_key,
          stock_id: e.stock_id,
          store_id: e.store_id,
          cost: e.cost,
          supplier_id: e.supplier_id,
          unit: e.unit,
          quantity: e.quantity,
          date_lost: e.date_lost,
          notes: e.notes,
        }
      })
      this.uploadData(data)
    },
    addTransaction() {
      const payload = {
        index_key: indexKeyGenerator(),
        store_id: this.storeId,
        stock_id: this.stockId,
        cost: this.cost,
        supplier_id: this.supplierId,
        unit: this.unit,
        quantity: this.quantity,
        date_lost: this.dateTransaction,
        notes: this.notes,
      }
      const supplierProduct = this.supplierProduct
      if (!this.lostInvLoading) {
        this.lostInvLoading = true
        this.lostInvErrors = []
        lostInvRepository.checkErrors(payload)
          .then(({ data }) => {
            this.addTempItem({
              ...payload,
              brand_name: supplierProduct.brand_name,
              product_name: supplierProduct.product_name,
              stock_code: supplierProduct.stock_code,
              totalAmount: payload.quantity * payload.cost,
            })
          })
          .catch(e => { this.lostInvErrors = errorHandler(e) })
          .then(() => { this.lostInvLoading = false })
      }
    },
    reset(v) {
      Object.keys(v).forEach(e => {
        this[e] = v[e]
      })
    },
    getSplrPd() {
      this.splrPdLoading = true
      splrPdSearchDelay(async () => {
        try {
          const { data } = await splrPdRepository.paginate(this.storeId, { search: this.searchInput })
          this.splrPdList = data
        } catch (e) {
          this.splrPdErrors = errorHandler(e)
        } finally {
          this.splrPdLoading = false
        }
      })
    },
    dateFormat(ndate) {
      const date = new Date(ndate)
      const year = date.getFullYear()
      const month = (`${date.getMonth() + 1}`).padStart(2, '0')
      const day = (`${date.getDate()}`).padStart(2, '0')

      return `${month}/${day}/${year}`
    },
    deleteTempUnit(index) {
      this.removeTempItem(index)
    },
  },
  mixins: [
    moneyMixin,
  ],
}
</script>
<style lang="scss" scoped>
.truncate {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
